/**
 * System Status - Display system health and connectivity status
 */

import React, { useState, useEffect } from 'react'
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Chip,
  LinearProgress,
  Alert,
  Stack,
  Button,
  CircularProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper
} from '@mui/material'
import {
  CheckCircle as CheckIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  Cloud as CloudIcon,
  Storage as DatabaseIcon,
  Api as ApiIcon,
  Speed as SpeedIcon,
  Refresh as RefreshIcon,
  WifiOff as OfflineIcon
} from '@mui/icons-material'
import { apiClient } from '../../../services/api'
import { getSystemIcon } from '../../../components/SystemIcons'

interface SystemHealth {
  system: string
  status: 'healthy' | 'degraded' | 'down'
  latency_ms: number
  last_check: string
  message?: string
}

interface OverallHealth {
  status: 'operational' | 'partial_outage' | 'major_outage'
  healthy_systems: number
  total_systems: number
  last_updated: string
  incidents: Incident[]
}

interface Incident {
  id: string
  severity: 'info' | 'warning' | 'critical'
  system: string
  message: string
  started_at: string
  resolved_at?: string
}

interface SystemStatusProps {
  fpCode: string
}

const SystemStatus: React.FC<SystemStatusProps> = ({ fpCode }) => {
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)
  const [systemHealth, setSystemHealth] = useState<SystemHealth[]>([])
  const [overallHealth, setOverallHealth] = useState<OverallHealth | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    fetchSystemStatus()
  }, [fpCode])

  const fetchSystemStatus = async () => {
    try {
      setLoading(true)
      setError(null)
      
      // Simulate API call - replace with actual endpoint
      const response = await apiClient.get(`/inspector/${fpCode}/system/health`)
      
      // Mock data for demonstration
      const mockHealth: SystemHealth[] = [
        { system: 'lightspeed', status: 'healthy', latency_ms: 120, last_check: new Date().toISOString() },
        { system: 'xero', status: 'healthy', latency_ms: 250, last_check: new Date().toISOString() },
        { system: 'database', status: 'healthy', latency_ms: 15, last_check: new Date().toISOString() },
        { system: 'api', status: 'healthy', latency_ms: 45, last_check: new Date().toISOString() }
      ]
      
      const mockOverall: OverallHealth = {
        status: 'operational',
        healthy_systems: 4,
        total_systems: 4,
        last_updated: new Date().toISOString(),
        incidents: []
      }
      
      setSystemHealth(mockHealth)
      setOverallHealth(mockOverall)
    } catch (err: any) {
      console.error('Error fetching system status:', err)
      setError('Failed to load system status')
    } finally {
      setLoading(false)
    }
  }

  const handleRefresh = async () => {
    setRefreshing(true)
    await fetchSystemStatus()
    setRefreshing(false)
  }

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'healthy':
      case 'operational':
        return <CheckIcon color="success" />
      case 'degraded':
      case 'partial_outage':
        return <WarningIcon color="warning" />
      case 'down':
      case 'major_outage':
        return <ErrorIcon color="error" />
      default:
        return <OfflineIcon color="disabled" />
    }
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'healthy':
      case 'operational':
        return 'success'
      case 'degraded':
      case 'partial_outage':
        return 'warning'
      case 'down':
      case 'major_outage':
        return 'error'
      default:
        return 'default'
    }
  }

  const getLatencyColor = (latency: number) => {
    if (latency < 100) return 'success'
    if (latency < 300) return 'warning'
    return 'error'
  }

  const getSystemDisplayIcon = (system: string) => {
    switch (system) {
      case 'database':
        return <DatabaseIcon />
      case 'api':
        return <ApiIcon />
      case 'cloud':
        return <CloudIcon />
      default:
        return getSystemIcon(system)
    }
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    )
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        {error}
      </Alert>
    )
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" fontWeight={600}>
          System Status
        </Typography>
        <Button
          startIcon={refreshing ? <CircularProgress size={20} /> : <RefreshIcon />}
          onClick={handleRefresh}
          disabled={refreshing}
        >
          Refresh
        </Button>
      </Box>

      {/* Overall Status */}
      {overallHealth && (
        <Card sx={{ mb: 3, bgcolor: getStatusColor(overallHealth.status) === 'success' ? 'success.lighter' : undefined }}>
          <CardContent>
            <Stack direction="row" spacing={2} alignItems="center">
              {getStatusIcon(overallHealth.status)}
              <Box sx={{ flexGrow: 1 }}>
                <Typography variant="h6">
                  System Status: {overallHealth.status === 'operational' ? 'All Systems Operational' : 
                               overallHealth.status === 'partial_outage' ? 'Partial System Outage' : 
                               'Major System Outage'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {overallHealth.healthy_systems} of {overallHealth.total_systems} systems healthy
                </Typography>
              </Box>
              <Chip
                label={overallHealth.status.replace('_', ' ').toUpperCase()}
                color={getStatusColor(overallHealth.status) as any}
                icon={getStatusIcon(overallHealth.status)}
              />
            </Stack>
          </CardContent>
        </Card>
      )}

      {/* Active Incidents */}
      {overallHealth && overallHealth.incidents.length > 0 && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          <Typography variant="subtitle2" fontWeight={600} gutterBottom>
            Active Incidents
          </Typography>
          <List dense>
            {overallHealth.incidents.map((incident) => (
              <ListItem key={incident.id}>
                <ListItemIcon>
                  {incident.severity === 'critical' ? <ErrorIcon color="error" /> :
                   incident.severity === 'warning' ? <WarningIcon color="warning" /> :
                   <InfoIcon color="info" />}
                </ListItemIcon>
                <ListItemText
                  primary={incident.message}
                  secondary={`${incident.system} - Started ${new Date(incident.started_at).toLocaleString()}`}
                />
              </ListItem>
            ))}
          </List>
        </Alert>
      )}

      {/* Individual System Status */}
      <Grid container spacing={2}>
        {systemHealth.map((system) => (
          <Grid item xs={12} sm={6} md={3} key={system.system}>
            <Card>
              <CardContent>
                <Stack spacing={2}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    {getSystemDisplayIcon(system.system)}
                    <Chip
                      size="small"
                      label={system.status}
                      color={getStatusColor(system.status) as any}
                    />
                  </Box>
                  
                  <Typography variant="h6">
                    {system.system.charAt(0).toUpperCase() + system.system.slice(1)}
                  </Typography>

                  <Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                      <Typography variant="caption" color="text.secondary">
                        Response Time
                      </Typography>
                      <Typography variant="caption" fontWeight={600}>
                        {system.latency_ms}ms
                      </Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={Math.min((300 - system.latency_ms) / 3, 100)}
                      color={getLatencyColor(system.latency_ms) as any}
                    />
                  </Box>

                  <Typography variant="caption" color="text.secondary">
                    Last checked: {new Date(system.last_check).toLocaleTimeString()}
                  </Typography>

                  {system.message && (
                    <Alert severity="info" sx={{ py: 0.5 }}>
                      {system.message}
                    </Alert>
                  )}
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Performance Metrics */}
      <Paper sx={{ mt: 3, p: 3 }}>
        <Typography variant="h6" gutterBottom>
          Performance Metrics
        </Typography>
        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <Stack spacing={1}>
              <Stack direction="row" spacing={1} alignItems="center">
                <SpeedIcon color="primary" />
                <Typography variant="subtitle2">Average Response Time</Typography>
              </Stack>
              <Typography variant="h4">
                {systemHealth.length > 0 
                  ? Math.round(systemHealth.reduce((acc, s) => acc + s.latency_ms, 0) / systemHealth.length)
                  : 0}ms
              </Typography>
            </Stack>
          </Grid>
          <Grid item xs={12} md={4}>
            <Stack spacing={1}>
              <Stack direction="row" spacing={1} alignItems="center">
                <CheckIcon color="success" />
                <Typography variant="subtitle2">Uptime</Typography>
              </Stack>
              <Typography variant="h4">99.9%</Typography>
            </Stack>
          </Grid>
          <Grid item xs={12} md={4}>
            <Stack spacing={1}>
              <Stack direction="row" spacing={1} alignItems="center">
                <ApiIcon color="info" />
                <Typography variant="subtitle2">API Calls Today</Typography>
              </Stack>
              <Typography variant="h4">12,543</Typography>
            </Stack>
          </Grid>
        </Grid>
      </Paper>
    </Box>
  )
}

// Add missing import
const InfoIcon = () => <WarningIcon />

export default SystemStatus